/**
 * Collapse
 * Animates a block open/closed and keeps following its content height.
 *
 * Unlike animating to `height: 'auto'`, the inner content is measured with a
 * ResizeObserver, so the wrapper also eases when the content changes while
 * open (skeleton -> loaded list, images finishing loading, nested panels).
 */

import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

type CollapseProps = {
  open: boolean;
  children: ReactNode;
  className?: string;
  id?: string;
};

const EASE_OUT_QUINT = [0.22, 1, 0.36, 1] as const;

export function Collapse({ open, children, className, id }: CollapseProps) {
  const reduceMotion = useReducedMotion();

  return (
    <AnimatePresence initial={false}>
      {open && (
        <CollapseBody
          key="collapse"
          className={className}
          id={id}
          reduceMotion={Boolean(reduceMotion)}
        >
          {children}
        </CollapseBody>
      )}
    </AnimatePresence>
  );
}

type CollapseBodyProps = Omit<CollapseProps, 'open'> & {
  reduceMotion: boolean;
};

function CollapseBody({
  children,
  className,
  id,
  reduceMotion,
}: CollapseBodyProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const measure = () => setHeight(el.offsetHeight);
    measure();

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <motion.div
      id={id}
      className={className}
      style={{ overflow: 'hidden' }}
      initial={{ height: 0, opacity: 0 }}
      animate={{ height, opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={
        reduceMotion
          ? { duration: 0 }
          : {
              height: { duration: 0.34, ease: EASE_OUT_QUINT },
              opacity: { duration: 0.22, ease: 'easeOut' },
            }
      }
    >
      {/* flow-root keeps child margins inside the measured box */}
      <div ref={contentRef} style={{ display: 'flow-root' }}>
        {children}
      </div>
    </motion.div>
  );
}
